import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  dispatcherListQuery,
  dispatchingExtraSourcesQuery,
  dispatchingShellQuery,
  dispatchingSubcategories,
  dispatchingTypeStatuses,
  saveTicketDispatching,
  type DispatcherOption,
  type DispatcherSaveRow,
  type MarkedRow,
  type NamedRow,
} from '../../api/settingsEaseedeskTicketDispatching'
import { LoadFailed } from '../../ui/LoadFailed'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Emails dispatching.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_dispatching/index.php` + `list.php`, si gest cu gest fata de
 * `index-1.js` si de scriptul din `list.php`:
 *
 *  - trei casute in cap: firma, sursa, si (numai la email si alerta) intrarea
 *    sursei; oricare se schimba, lista se cere din nou;
 *  - setarile de sus (`order_id = 0`) stau intr-un bloc despartit de linia
 *    punctata, si NU se trag cu mouse-ul;
 *  - restul se trag unul peste altul, iar numarul din bulina e chiar `order_id`
 *    care se salveaza: primul aprins e cel care castiga;
 *  - casuta de status se reincarca dupa cea de tip, si cea de subcategorie
 *    dupa cea de categorie - exact ca cele doua chemari ajax vechi.
 *
 * MARKUPUL RANDULUI SE SCRIE AICI, nu vine din PHP; in portal era construit in
 * macheta, cu `$$field` ca nume de variabila.
 */
export function TicketDispatchingPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(dispatchingShellQuery())

  const [company, setCompany] = useState(0)
  const [source, setSource] = useState(0)
  const [option, setOption] = useState(0)

  /* Sursa de pornire e prima din lista, ca `<select>`-ul vechi care arata
     prima optiune si trimitea imediat `change` pe firma. */
  useEffect(() => {
    if (source === 0 && shell.data && shell.data.sources.length > 0) {
      setSource(shell.data.sources[0].ID)
    }
  }, [shell.data, source])

  const chosenSource = shell.data?.sources.find((s) => s.ID === source)
  const hasOptions = chosenSource?.extra_source === true

  const extra = useQuery({
    ...dispatchingExtraSourcesQuery(source),
    enabled: hasOptions && source > 0,
  })

  /* Intrarea de pornire e prima din lista, ca in `index-1.js` (`i == 0 ?
     selected`). Cand sursa n-are intrari, e 0. */
  useEffect(() => {
    if (!hasOptions) {
      setOption(0)
      return
    }
    const options = extra.data?.options ?? []
    if (options.length > 0 && !options.some((o) => o.value === option)) {
      setOption(options[0].value)
    }
  }, [hasOptions, extra.data, option])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const ready = source > 0 && (!hasOptions || !extra.isLoading)

  return (
    <div>
      <h4>
        <strong>Ticket</strong> dispatching
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage tickets opened from <strong>emails, mobile apps, agent, remote auto setup and oncall.</strong>
      </label>
      <hr />

      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-12">
          <div className="form-inline">
            <div className="form-group col-md-3">
              <label>Company</label>
              <SelectPicker
                id="dispatching-company"
                className="form-control input-sm"
                title="All companies"
                value={String(company)}
                options={[
                  { value: '0', label: 'All companies' },
                  ...shell.data.companies.map((c) => ({ value: String(c.ID), label: c.name })),
                ]}
                onChange={(v) => setCompany(Number(v))}
              />
            </div>
            <div className="form-group col-md-2">
              <label>Ticket source</label>
              <SelectPicker
                id="dispatching-source"
                className="form-control input-sm"
                title="Ticket source"
                value={String(source)}
                options={shell.data.sources.map((s) => ({ value: String(s.ID), label: s.source_name }))}
                onChange={(v) => setSource(Number(v))}
              />
            </div>
            {hasOptions && (extra.data?.options.length ?? 0) > 0 ? (
              <div className="form-group col-md-2">
                <label>Source option</label>
                <SelectPicker
                  id="dispatching-source-option"
                  className="form-control input-sm"
                  title="Source option"
                  value={String(option)}
                  options={(extra.data?.options ?? []).map((o) => ({
                    value: String(o.value),
                    label: o.name,
                  }))}
                  onChange={(v) => setOption(Number(v))}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {ready ? (
        <DispatcherList
          company={company}
          source={source}
          option={option}
          onSaved={() => {
            void queryClient.invalidateQueries({
              queryKey: ['settings', 'easeedesk', 'ticket_dispatching', 'list'],
            })
          }}
        />
      ) : (
        <Spinner />
      )}

      <div className="well corner-flip flip-bg-white" data-color="inverse">
        <h4>
          <i className="fas fa-info-circle"></i> <strong>NOTE</strong>
        </h4>
        <p>
          Drag the elements in order to arrange them by priority.{' '}
          <strong>First active element will be top priority.</strong>
        </p>
        <div className="flip"></div>
      </div>
    </div>
  )
}

/** Un rand de lucru: optiunea, cu ce a schimbat omul in ea, inainte de salvare. */
interface Row extends DispatcherOption {
  key: number
}

function DispatcherList({
  company,
  source,
  option,
  onSaved,
}: {
  company: number
  source: number
  option: number
  onSaved(): void
}) {
  const list = useQuery(dispatcherListQuery(company, source, option))

  const [rows, setRows] = useState<Row[]>([])
  const [statuses, setStatuses] = useState<MarkedRow[]>([])
  const [subcategories, setSubcategories] = useState<MarkedRow[]>([])
  const [dragged, setDragged] = useState<number | null>(null)

  useEffect(() => {
    if (!list.data) return
    setRows(list.data.options.map((o) => ({ ...o, key: o.dispatcher_id })))
  }, [list.data])

  /* Cele doua liste care atarna de casuta de deasupra. In portal se cereau
     odata la incarcare si apoi la fiecare `changed.bs.select`. */
  const typeValue = rows.find((r) => r.classes === 'ticket-type-event')?.select_value ?? 0
  const categoryValue = rows.find((r) => r.classes === 'ticket-category-event')?.select_value ?? 0
  const hasStatusRow = rows.some((r) => r.classes === 'ticket-status-values')
  const hasSubcategoryRow = rows.some((r) => r.classes === 'ticket-subcategory-values')

  useEffect(() => {
    if (!hasStatusRow) {
      setStatuses([])
      return
    }
    let alive = true
    void dispatchingTypeStatuses(typeValue, source, option, company).then((r) => {
      if (alive) setStatuses(r.statuses)
    })
    return () => {
      alive = false
    }
  }, [hasStatusRow, typeValue, source, option, company])

  useEffect(() => {
    if (!hasSubcategoryRow) {
      setSubcategories([])
      return
    }
    let alive = true
    void dispatchingSubcategories(categoryValue, source, option, company).then((r) => {
      if (alive) setSubcategories(r.subcategories)
    })
    return () => {
      alive = false
    }
  }, [hasSubcategoryRow, categoryValue, source, option, company])

  const save = useMutation({
    mutationFn: () => {
      const payload: DispatcherSaveRow[] = rows.map((row) => ({
        dispatcher_id: row.dispatcher_id,
        order_id: row.order_id,
        value: row.select_value,
        checked: row.checked,
      }))
      return saveTicketDispatching(company, source, option, payload)
    },
    onSuccess: () => {
      toast('Settings changed', 'Success', 'success')
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const lists = useMemo(
    () => ({
      ticket_types: list.data?.ticket_types ?? [],
      ticket_priorities: list.data?.ticket_priorities ?? [],
      ticket_categories: list.data?.ticket_categories ?? [],
      groups: list.data?.groups ?? [],
      administrators: list.data?.administrators ?? [],
      ticket_statuses: statuses.map((s): NamedRow => ({ ID: s.ID, name: s.name })),
      ticket_subcategories: subcategories.map((s): NamedRow => ({ ID: s.ID, name: s.name })),
    }),
    [list.data, statuses, subcategories],
  )

  if (list.error instanceof ApiError && list.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (list.error instanceof ApiError && list.error.status === 404) {
    return <div className="alert alert-warning">Unknown company.</div>
  }
  if (list.error) {
    return <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The dispatchers" />
  }
  if (!list.data) return <Spinner />

  const change = (key: number, next: Partial<Row>) => {
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...next } : r)))
  }

  const top = rows.filter((r) => r.order_id === 0)
  const sortable = rows.filter((r) => r.order_id > 0).sort((a, b) => a.order_id - b.order_id)

  /* Tragerea rescrie `order_id` pe toate, de la 1 in sus - exact ce facea
     `sortable().on('click')` cu bulina si cu `input.order`. */
  const move = (from: number, to: number) => {
    const next = [...sortable]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    const renumbered = new Map(next.map((r, i) => [r.key, i + 1]))
    setRows((current) =>
      current.map((r) => (renumbered.has(r.key) ? { ...r, order_id: renumbered.get(r.key)! } : r)),
    )
  }

  return (
    <div>
      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-12">
          <button
            className="btn btn-primary pull-right update-dispatching-settings"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Update settings
          </button>
        </div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0 }}>
        {top.map((row) => (
          <li
            key={row.key}
            style={{
              padding: '10px 20px',
              marginBottom: 4,
              borderRadius: 4,
              borderWidth: '1px 6px',
              borderColor: '#ddd',
              borderStyle: 'solid',
            }}
          >
            <div className="row">
              <div className="col-md-3">
                <div style={{ minWidth: 50, float: 'left' }}>
                  <i className="fas fa-exclamation-circle font-24 color-red"></i>
                </div>
                <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                  <i
                    className="fas fa-info-circle color-blue font-14"
                    style={{ cursor: 'default' }}
                    title={row.info}
                  >
                    {' '}
                  </i>{' '}
                  {row.dispatcher_name}
                </h5>
              </div>
              <div className="col-md-4">
                <ValuePicker row={row} lists={lists} onChange={(v) => change(row.key, { select_value: v })} />
              </div>
              <div className="col-md-3"></div>
              <div className="col-md-2">
                <div className="pull-right">
                  <input
                    type="checkbox"
                    className="switchery_edit"
                    checked={row.checked}
                    onChange={(e) => change(row.key, { checked: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <hr className="dotted" />

      <ul className="list-group-sortable-connected" style={{ listStyle: 'none', padding: 0 }}>
        {sortable.map((row, index) => (
          <li
            key={row.key}
            className="list-li dispatching-list"
            draggable
            onDragStart={() => setDragged(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragged === null || dragged === index) return
              move(dragged, index)
              setDragged(null)
            }}
            style={{ cursor: 'move' }}
          >
            <div className="row">
              <div className="col-md-3">
                <div style={{ minWidth: 50, float: 'left' }}>
                  <span className="badge pull-left" style={{ minWidth: 23, backgroundColor: '#6f7b8a', color: '#fff' }}>
                    {row.order_id}
                  </span>
                </div>
                <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                  <i
                    className="fas fa-info-circle color-blue font-14"
                    style={{ cursor: 'default' }}
                    title={row.info}
                  >
                    {' '}
                  </i>{' '}
                  {row.dispatcher_name}
                </h5>
              </div>
              <div className="col-md-4">
                <ValuePicker row={row} lists={lists} onChange={(v) => change(row.key, { select_value: v })} />
              </div>
              <div className="col-md-3">
                {row.fields
                  .filter((field) => field.field_type === 'select')
                  .map((field) => (
                    /* Campurile suplimentare se ARATA si nu se salveaza:
                       `Ticket_dispatching_scheme::save()` primeste
                       `$extra_fields` si nu-l atinge. De-aia sunt inchise. */
                    <select key={field.ID} className="form-control" disabled>
                      {field.values.map((value) => (
                        <option key={value.ID} value={value.option_value}>
                          {value.option_name}
                        </option>
                      ))}
                    </select>
                  ))}
              </div>
              <div className="col-md-2">
                <div className="pull-right">
                  <input
                    type="checkbox"
                    className="switchery_edit"
                    checked={row.checked}
                    onChange={(e) => change(row.key, { checked: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Casuta unui repartizator. `select_field` spune din ce lista se alege - in
 * macheta veche era chiar numele variabilei PHP (`$$field`).
 *
 * Cand randul are deja o optiune scrisa in catalog (`option_name`), ea sta
 * prima in lista, ca in portal.
 */
function ValuePicker({
  row,
  lists,
  onChange,
}: {
  row: DispatcherOption
  lists: Record<string, NamedRow[]>
  onChange(value: number | null): void
}) {
  if (row.select_field === null) return null

  const rows = lists[row.select_field] ?? []
  const options = [
    row.option_name !== null && row.option_name !== ''
      ? { value: String(row.option_value ?? ''), label: row.option_name }
      : { value: '', label: 'Select..' },
    ...rows.map((r) => ({ value: String(r.ID), label: r.name })),
  ]

  return (
    <SelectPicker
      id={`dispatcher-${row.dispatcher_id}`}
      className={`form-control ${row.classes}`}
      title="Select.."
      value={row.select_value === null ? '' : String(row.select_value)}
      options={options}
      onChange={(v) => onChange(v === '' ? null : Number(v))}
    />
  )
}
